import { AuditState, AuditRecordDto } from '@lmt/shared-types';
import { format } from 'date-fns';
import { isEmpty, isEqual } from 'lodash';

const customFields = 'Values';

export function convertAuditRecord(record: AuditRecordDto) {
  const state = record.state;
  const oldValues = flattenCustomFields(record.oldValues || {});
  const newValues = flattenCustomFields(record.newValues || {});
  let changes: { [key: string]: string } = {};
  switch (state) {
    case AuditState.Added: {
      Object.keys(newValues).forEach((key) => {
        changes[key] = valueToString(newValues[key]);
      });
      break;
    }

    case AuditState.Deleted: {
      Object.keys(oldValues).forEach((key) => {
        changes[key] = valueToString(oldValues[key]);
      });
      break;
    }

    case AuditState.Modified: {
      const allFields = new Set([
        ...Object.keys(oldValues),
        ...Object.keys(newValues),
      ]);
      allFields.forEach((field) => {
        const oldValue = valueToString(oldValues[field]);
        const newValue = valueToString(newValues[field]);

        if (!isEqual(oldValue, newValue)) {
          if (
            oldValue === null ||
            oldValue === undefined ||
            isEmpty(oldValue.toString())
          ) {
            changes[field] = `added "${newValue}"`;
          } else if (
            newValue === null ||
            newValue === undefined ||
            isEmpty(newValue.toString())
          ) {
            changes[field] = `deleted "${oldValue}"`;
          } else {
            changes[field] = `${oldValue} -> ${newValue}`;
          }
        }
      });
      break;
    }
    default: {
      break;
    }
  }

  changes = filterEmptyValues(changes);

  return {
    id: record.id,
    dateTime: record.dateTime,
    rowKey: record.rowKey,
    tableName: record.tableName,
    patientId: record.patientId,
    patientName: record.patientName,
    state: record.state,
    userId: record.userId,
    userName: record.userName,
    changes,
  };
}

function valueToString(value: any) {
  if (value === null || value === undefined || isEmpty(value.toString()))
    return "''";
  if (Array.isArray(value)) {
    if (typeof value[0] === 'object') {
      // this is Table
      const stringTable = value
        .map((row) => {
          return (
            '- ' +
            Object.keys(row)
              .filter((key) => !key.startsWith('__'))
              .map((key) => `${key}: ${row[key]}`)
              .join(', ')
          );
        })
        .join('\n');
      return '\n' + stringTable + '\n';
    }
    return value.join(', ');
  }

  if (value instanceof String) {
    return `'${value}'`;
  }
  return value;
}

function flattenCustomFields(values: AuditRecordValues) {
  let result = values;
  const customFieldsObject = values[customFields];
  if (customFieldsObject) {
    result = Object.assign({}, values, customFieldsObject);
    delete result[customFields];
  }
  return result;
}

function filterEmptyValues(changes: AuditRecordValues) {
  const result: { [key: string]: any } = {};
  Object.keys(changes).forEach((field) => {
    const value = changes[field];
    if (value && (field !== customFields || !isEmpty(value))) {
      result[field] = value;
    }
  });
  return result;
}

type AuditRecordValues = { [key: string]: any | undefined };
